import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  clearAllStateInItemSalesSlice,
  fetchItemSalesData,
} from "../../../Redux/features/Reports/ItemSales/ItemSalesSlice";
import { useAuthDetails } from "../../../Common/cookiesHelper";
import { Grid } from "@mui/material";

import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { priceFormate } from "../../../hooks/priceFormate";
import { SkeletonTable } from "../../../reuseableComponents/SkeletonTable";
import sortIcon from "../../../Assests/Category/SortingW.svg";
import { SortTableItemsHelperFun } from "../../../helperFunctions/SortTableItemsHelperFun";
import PasswordShow from "../../../Common/passwordShow";
import NoDataFound from "../../../reuseableComponents/NoDataFound";
import useDelayedNodata from "../../../hooks/useDelayedNoData";
import { formatCurrency } from "../../../Constants/utils";
import InputTextSearch from "../../../reuseableComponents/InputTextSearch";
import useDebounce from "../../../hooks/useDebouncs";
import { updateReportPayload } from "../../../Redux/features/Reports/SalesReport/SalesReportSlice";
import ItemSalesColumnPicker from "./ItemSalesColumnPicker";
import {
  parseCustomCode,
  serializeCustomCodes,
} from "../../Products/customCodeUtils";

const EXTRA_COLUMN_OPTIONS = [
  { id: "upc", name: "UPC" },
  { id: "custom_code", name: "Custome Code" },
  // { id: "barcode", name: "Barcode" }, // Hide Barcode column as per the requirement
];

const getUpcValue = (item) =>
  item?.var_upc ? item.var_upc : item?.upc || "";

const getCustomCodeValue = (item) => {
  const raw =
    item?.var_custom_code != null && String(item.var_custom_code).trim() !== ""
      ? item.var_custom_code
      : item?.custom_code;
  // API stores multiple codes pipe-separated, e.g.
  // 544545656589585|549564846544644|332352659595954
  return serializeCustomCodes(parseCustomCode(raw));
};

const getBarcodeValue = (item) =>
  item?.var_barcode ? item.var_barcode : item?.barcode || "";

// react-csv wraps every cell in quotes but does not escape internal `"` characters.
// Pre-escape them so names like ALeaf 5" Supernova... stay in one column.
const escapeCsvField = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).replace(/"/g, '""');
};

const buildCsvRow = (item, columns) => {
  const row = {};
  columns.forEach((col) => {
    let value = "";
    switch (col.name) {
      case "name":
        value = item?.name ?? "";
        break;
      case "categoryss":
        value = item?.categoryss ?? "";
        break;
      case "upc":
        value = getUpcValue(item);
        break;
      case "custom_code":
        value = getCustomCodeValue(item);
        break;
      case "barcode":
        value = getBarcodeValue(item);
        break;
      case "total_qty":
        value = !!item?.total_qty ? parseInt(item?.total_qty) : "0";
        break;
      case "total_price":
        value = !!item?.total_price
          ? `${priceFormate(formatCurrency(item?.total_price))}`
          : "$0.00";
        break;
      case "adjust_price":
        value = !!item?.adjust_price
          ? `${priceFormate(formatCurrency(item?.adjust_price))}`
          : "$0.00";
        break;
      case "discount_amt":
        value = `$${priceFormate(parseFloat(item?.discount_amt).toFixed(2))}`;
        break;
      case "saletx":
        value = `$${priceFormate(parseFloat(item?.saletx))}`;
        break;
      case "othertx":
        value = `$${priceFormate(parseFloat(item?.othertx))}`;
        break;
      case "refund_amount":
        value = `$${priceFormate(parseFloat(item?.refund_amount).toFixed(2))}`;
        break;
      case "discount_price":
        value = !!item?.discount_price
          ? `${priceFormate(formatCurrency(item?.discount_price))}`
          : "$0.00";
        break;
      default:
        value = item?.[col.name] ?? "";
    }
    row[col.name] = escapeCsvField(value);
  });
  return row;
};

const BASE_TABLE_ROWS = [
  { type: "str", name: "name", label: "Name" },
  { type: "str", name: "categoryss", label: "Category" },
  { type: "num", name: "total_qty", label: "Quantity Sold" },
  { type: "num", name: "total_price", label: "Gross Sales" },
  { type: "num", name: "adjust_price", label: "Price Override" },
  { type: "num", name: "discount_amt", label: "Discounts" },
  { type: "num", name: "saletx", label: "Default Tax" },
  { type: "num", name: "othertx", label: "Other Tax" },
  { type: "num", name: "refund_amount", label: "Refunded" },
  { type: "num", name: "discount_price", label: "Net Sales" },
];

const StyledTable = styled(Table)(({ theme }) => ({
  padding: 2, // Adjust padding as needed
}));
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--qv-bg-253338, #253338)",
    color: theme.palette.common.white,
    fontFamily: "CircularSTDMedium !important",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "CircularSTDBook !important",
  },
  [`&.${tableCellClasses.table}`]: {
    fontSize: 14,
    fontFamily: "CircularSTDBook !important",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {},
  "& td, & th": {
    border: "none",
  },
}));

const ItemSalesDetails = ({
  allItemSalesData,
  setallItemSalesData,
  ...props
}) => {
  const dispatch = useDispatch();
  const { LoginGetDashBoardRecordJson, LoginAllStore, userTypeData } =
    useAuthDetails();
  const { handleCoockieExpire, getUnAutherisedTokenMessage, getNetworkError } =
    PasswordShow();
  const [sortOrder, setSortOrder] = useState("asc");

  const [apiStatus, setapiStatus] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState({
    upc: false,
    custom_code: false,
    barcode: false,
  });
  const [appliedColumns, setAppliedColumns] = useState({
    upc: false,
    custom_code: false,
    barcode: false,
  });
  const AllItemSalesDataState = useSelector(
    (state) => state.ItemSalesReportList
  );

  const tableRow = useMemo(() => {
    const rows = [...BASE_TABLE_ROWS];
    const extras = [];
    if (appliedColumns.upc) {
      extras.push({ type: "str", name: "upc", label: "UPC" });
    }
    if (appliedColumns.custom_code) {
      extras.push({
        type: "str",
        name: "custom_code",
        label: "Custome Code",
      });
    }
    if (appliedColumns.barcode) {
      extras.push({ type: "str", name: "barcode", label: "Barcode" });
    }
    // Insert optional columns immediately after Name
    rows.splice(1, 0, ...extras);
    return rows;
  }, [appliedColumns]);

  const applyColumns = () => {
    setAppliedColumns({ ...selectedColumns });
  };

  // Place "+" after Name and any applied optional columns (UPC / Custome Code / Barcode)
  const plusAfterColumnName = appliedColumns.barcode
    ? "barcode"
    : appliedColumns.custom_code
      ? "custom_code"
      : appliedColumns.upc
        ? "upc"
        : "name";

  let merchant_id = LoginGetDashBoardRecordJson?.data?.merchant_id;
  useEffect(() => {
    getFetchItemSalesData();
    // console.log("All Props",props)
  }, [
    props.selectedDateRange,
    props.OrderSourceData,
    props.OrderTypeData,
    props.SelectCatData,
    props.items,
    props.brand,
    props.tag,
    props.selectedCategoryList,
    props.selectedBrandList,
    props.selectedTagList,
  ]);
  // console.log("props.selectedTagList,",props.selectedTagList)
  // console.log("props.allTagSelected,,",props.allTagSelected)
  const getFetchItemSalesData = async () => {
    if (
      props &&
      props.selectedDateRange &&
      (props.selectedCategoryList.length !== 0 ||
        props?.selectedBrandList.length !== 0 ||
        props?.selectedTagList.length !== 0)
    ) {
      try {
        let data = {
          merchant_id,
          start_date: props.selectedDateRange.start_date,
          end_date: props.selectedDateRange.end_date,
          order_typ: props.OrderTypeData,
          order_env: props.OrderSourceData,
          // search_by_brand: props.brand === "All" ? "" : props.brand,
          // search_by_tag: props.tag === "All" ? "" : props.tag,
          // cat_name:
          //   Array.isArray(props.SelectCatData) &&
          //   props.SelectCatData.length > 0 &&
          //   !props.allOptionSelected
          //     ? props.SelectCatData.map((emp) => emp.title).toString()
          //     : "All",
          cat_name:
            Array.isArray(props.selectedCategoryList) &&
              props.selectedCategoryList.length > 0 &&
              !props.allCategorySelected
              ? // ? props.selectedCategoryList.map((cat) => `${cat.id}`).join(",")
              props.selectedCategoryList.map((cat) => cat?.id).toString()
              : "All",
          // search_by_brand:
          //   Array.isArray(props.selectedBrandList) &&
          //   props.selectedBrandList.length > 0 &&
          //   !props.allBrandSelected
          //     ? props.selectedBrandList
          //         .map((brand) => `${brand.title}`)
          //         .join(",")
          //     : props.selectedBrandList
          //         .map((brand) => `${brand.title}`)
          //         .join(","),
          // search_by_tag:
          //   Array.isArray(props.selectedTagList) &&
          //   props.selectedTagList.length > 0 &&
          //   !props.allTagSelected
          //     ? props.selectedTagList.map((tag) => `${tag.title}`).join(",")
          //     : props.selectedTagList.map((tag) => `${tag.title}`).join(","),
          // search_by: props.items,
          search_by_brand: props?.selectedBrandList
            ?.map((brand) => `${brand?.title}`)
            ?.join(","),
          search_by_tag: props?.selectedTagList
            ?.map((tag) => `${tag?.title}`)
            ?.join(","),
          search_by: props.debouncedValue,
          ...userTypeData,
        };
        if (data) {
          dispatch(updateReportPayload(data));
          await dispatch(fetchItemSalesData(data)).unwrap();
          setapiStatus(false);
        }
      } catch (error) {
        if (error?.status == 401) {
          getUnAutherisedTokenMessage();
          handleCoockieExpire();
        }
      }
    } else {
      dispatch(clearAllStateInItemSalesSlice());
    }
  };

  useEffect(() => {
    if (
      !AllItemSalesDataState.loading &&
      AllItemSalesDataState.ItemSalesData &&
      AllItemSalesDataState.ItemSalesData[0]
    ) {
      const itemSalesRows = AllItemSalesDataState.ItemSalesData[0].map(
        (item) => ({
          ...item,
          upc: getUpcValue(item),
          custom_code: getCustomCodeValue(item),
          barcode: getBarcodeValue(item),
        })
      );
      setallItemSalesData(itemSalesRows);
      setapiStatus(AllItemSalesDataState.ItemSalesData[3]);

      const csvData = itemSalesRows;

      if (csvData.length > 0) {
        const data = csvData.map((item) => buildCsvRow(item, tableRow));

        props.setCSVData(data);
        props.setCSVHeader(
          tableRow.map((row) => ({ label: row.label, key: row.name }))
        );
        props.setCSVFileName("sales_by_items");
      }
    } else {
      props.setCSVHeader([]);
      setallItemSalesData([]);
      props.setCSVFileName("");

      // setapiStatus(false);
      !AllItemSalesDataState?.ItemSalesData && setapiStatus(false);
    }
  }, [AllItemSalesDataState, AllItemSalesDataState.ItemSalesData, tableRow]);

  const sortByItemName = (type, name) => {
    const { sortedItems, newOrder } = SortTableItemsHelperFun(
      allItemSalesData,
      type,
      name,
      sortOrder
    );
    setallItemSalesData(sortedItems);
    setSortOrder(newOrder);
  };

  const handleKeyDown = (e) => {
    handleSearchInputChange(e.target.value);
    if (e.key === "Enter") {
      e.target.blur();
    }
  };

  const handleSearchInputChange = (value) => {
    props.setSearchRecord(value);
  };

  return (
    <>
      <Grid container className="box_shadow_div">
        <Grid item xs={12}>
          {/* <div className="q-attributes-bottom-header">
            <span>Item Sales Report</span>
          </div> */}
          <Grid item xs={12} sx={{ p: { xs: 1, sm: 2.5 } }}>
            <InputTextSearch
              placeholder="Search by Items Name"
              value={props.searchRecord}
              handleChange={handleSearchInputChange}
              handleSearchButton={props.handleSearch}
              handlefocus={handleKeyDown}
            />
          </Grid>

          {(AllItemSalesDataState.loading || AllItemSalesDataState.status) &&
            allItemSalesData.length <= 0 ? (
            <SkeletonTable columns={tableRow.map((item) => item.label)} />
          ) : (
            <TableContainer
              className="custom-scroll-hidden"
              sx={{ maxHeight: "80vh" }}
            >
              <StyledTable
                stickyHeader
                sx={{ minWidth: 500 }}
                aria-label="customized table"
              >
                <TableHead>
                  {tableRow.map((item) => (
                    <React.Fragment key={item.name}>
                      <StyledTableCell>
                        <button
                          className="flex items-center"
                          onClick={() => sortByItemName(item.type, item.name)}
                        >
                          <p>{item.label}</p>
                          <img src={sortIcon} alt="" className="pl-1" />
                        </button>
                      </StyledTableCell>
                      {item.name === plusAfterColumnName && (
                        <StyledTableCell
                          sx={{
                            width: "40px",
                            minWidth: "40px",
                            maxWidth: "40px",
                            padding: "8px 4px",
                            verticalAlign: "middle",
                            textAlign: "center",
                          }}
                        >
                          <ItemSalesColumnPicker
                            columnsOptions={EXTRA_COLUMN_OPTIONS}
                            selectedColumns={selectedColumns}
                            setSelectedColumns={setSelectedColumns}
                            applyColumns={applyColumns}
                          />
                        </StyledTableCell>
                      )}
                    </React.Fragment>
                  ))}
                </TableHead>
                <TableBody>
                  {allItemSalesData &&
                    allItemSalesData.length >= 1 &&
                    allItemSalesData.map((ItemData, rowIndex) => (
                      <StyledTableRow key={rowIndex}>
                        <StyledTableCell>
                          <p>{ItemData.name}</p>
                        </StyledTableCell>
                        {appliedColumns.upc && (
                          <StyledTableCell>
                            <p>{getUpcValue(ItemData)}</p>
                          </StyledTableCell>
                        )}
                        {appliedColumns.custom_code && (
                          <StyledTableCell
                            sx={{ minWidth: 180, maxWidth: 320 }}
                          >
                            <p
                              style={{
                                margin: 0,
                                wordBreak: "break-all",
                                whiteSpace: "normal",
                              }}
                            >
                              {getCustomCodeValue(ItemData)}
                            </p>
                          </StyledTableCell>
                        )}
                        {appliedColumns.barcode && (
                          <StyledTableCell>
                            <p>{getBarcodeValue(ItemData)}</p>
                          </StyledTableCell>
                        )}
                        <StyledTableCell />
                        <StyledTableCell>
                          <p>{ItemData.categoryss}</p>
                        </StyledTableCell>
                        <StyledTableCell>
                          <p>
                            {/* {priceFormate(ItemData.total_qty || 0)} */}
                            {!!ItemData.total_qty
                              ? parseInt(ItemData?.total_qty)
                              : // priceFormate(formatCurrency(ItemData.total_qty))
                              "0"}
                          </p>
                        </StyledTableCell>
                        <StyledTableCell>
                          <p>
                            {/* ${priceFormate(ItemData.total_price)} */}
                            {!!ItemData?.total_price
                              ? priceFormate(
                                formatCurrency(ItemData?.total_price)
                              )
                              : "$0.00"}
                          </p>
                        </StyledTableCell>
                        <StyledTableCell>
                          <p>
                            {/* ${priceFormate(ItemData.adjust_price)} */}
                            {!!ItemData.adjust_price
                              ? priceFormate(
                                formatCurrency(ItemData.adjust_price)
                              )
                              : "$0.00"}
                          </p>
                        </StyledTableCell>
                        <StyledTableCell>
                          <p>
                            {/* ${priceFormate(ItemData.discount_amt)} */}
                            {!!ItemData.discount_amt
                              ? priceFormate(
                                formatCurrency(ItemData.discount_amt)
                              )
                              : "$0.00"}
                          </p>
                        </StyledTableCell>
                        <StyledTableCell>
                          <p>${priceFormate(ItemData.saletx)}</p>
                        </StyledTableCell>
                        <StyledTableCell>
                          <p>${priceFormate(ItemData.othertx)}</p>
                        </StyledTableCell>
                        <StyledTableCell>
                          <p>${priceFormate(ItemData.refund_amount)}</p>
                        </StyledTableCell>
                        <StyledTableCell>
                          <p>
                            {!!ItemData.discount_price
                              ? priceFormate(
                                formatCurrency(ItemData.discount_price)
                              )
                              : "$0.00"}
                          </p>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                </TableBody>
              </StyledTable>

              {!AllItemSalesDataState.loading &&
                !AllItemSalesDataState.status &&
                allItemSalesData?.length <= 0 && <NoDataFound />}
            </TableContainer>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default ItemSalesDetails;
